import { FC, Fragment, useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Collapse,
  Typography,
} from '@mui/material';
import DetectionDetails from '../DetectHoleTab/DetectionDetails';
import WarningIcon from '@mui/icons-material/WarningAmberRounded';
import { HoleDectionListItem } from 'models/holeDetection';
import { t } from 'i18next';
import useBaseLinks from 'store/Auth/Links/useBaseLinks';
import { useGetHoleClassificationsQuery } from 'store/_Api/HoleDetection';
import { skipToken } from '@reduxjs/toolkit/query';

type PropsType = {
  items: HoleDectionListItem[];
  isHistory?: boolean;
};

export const getFormatedDateTime = (timestamp: number): string => {
  const d = new Date(timestamp * 1000);
  const pad = (n: number) => n.toString().padStart(2, '0');
  return `${pad(d.getHours())}:${pad(d.getMinutes())} - ${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()}`;
};

const HoleDetectionTable: FC<PropsType> = ({
  items,
  isHistory = false,
}): JSX.Element => {
  const [expandedRow, setExpandedRow] = useState(null);
  const { getLink } = useBaseLinks();
  const classificationLink = getLink('hole-detection-classifications') ?? '';
  const { data: classificationData } = useGetHoleClassificationsQuery(
    classificationLink ?? skipToken,
  );

  const handleRowClick = (rowId) => {
    setExpandedRow(expandedRow === rowId ? null : rowId);
  };

  const getDynamicColor = (percentageInPoints) => {
    const percentage = percentageInPoints * 100;
    if (percentage <= 25) {
      const shade = 128 + Math.round((percentage / 25) * 127);
      return `rgb(${shade}, ${shade}, ${shade})`;
    } else if (percentage <= 50) {
      const red = 255;
      const green = 255 - Math.round(((percentage - 25) / 25) * 90);
      return `rgb(${red}, ${green}, 0)`;
    } else if (percentage <= 75) {
      const green = 165 - Math.round(((percentage - 50) / 25) * 165);
      return `rgb(255, ${green}, 0)`;
    }
    return 'rgb(255, 0, 0)';
  };

  const getFullLocationName = (item) => {
    const locationParts = [
      item.customer_name,
      item.location_name,
      item.site_name,
      item.pen_code ? `P${item.pen_code}` : null,
      item.deployment_point_name,
    ];

    const locationString = locationParts.filter((part) => part).join(' / ');

    return locationString;
  };

  const getClassificationName = (classificationId?: number) => {
    const classificationLabel = classificationData?.collection?.find(
      ({ classification }) => classification === classificationId,
    )?.label;
    return classificationLabel
      ? classificationLabel
          .split('_')
          .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
          .join(' ')
      : 'N/A';
  };

  return (
    <TableContainer className="detect-hole">
      <Table>
        <TableHead>
          <TableRow>
            <TableCell
              style={{
                width: '30%',
              }}
            >
              {t('Location')}
            </TableCell>
            <TableCell align="center">{t('Confidence level')}</TableCell>
            <TableCell align="center">{t('Classification')}</TableCell>
            <TableCell>{t('Time')}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {items.length === 0 ? (
            <TableRow>
              <TableCell
                colSpan={4}
                style={{ textAlign: 'center', padding: '16px' }}
              >
                {t('No results found')}
              </TableCell>
            </TableRow>
          ) : (
            items.map((row, index) => {
              const id = row?.image_id;

              return (
                <Fragment key={index}>
                  <TableRow
                    hover
                    onClick={() => handleRowClick(id)}
                    style={{
                      cursor: 'pointer',
                      backgroundColor: expandedRow === id ? '#d6d8d6' : 'unset',
                    }}
                  >
                    <TableCell>{getFullLocationName(row)}</TableCell>
                    <TableCell
                      align="center"
                      sx={{
                        justifyContent: 'center',
                        display: 'flex',
                        gap: '8px',
                      }}
                    >
                      <WarningIcon
                        sx={{
                          color: getDynamicColor(row?.ai_confidence),
                        }}
                      />
                      <Typography
                        sx={{
                          fontSize: '14px',
                          lineHeight: '14px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        {`${row?.ai_confidence * 100}%`}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      {getClassificationName(
                        isHistory
                          ? row?.actual_risk_classification
                          : row?.ai_risk_classification,
                      ) || 'N/A'}
                    </TableCell>
                    <TableCell>{getFormatedDateTime(row?.timestamp)}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell colSpan={4} style={{ padding: 0 }}>
                      <Collapse
                        in={expandedRow === id}
                        timeout="auto"
                        unmountOnExit
                      >
                        <div style={{ padding: '16px' }}>
                          <DetectionDetails item={row} isHistory={isHistory} />
                        </div>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </Fragment>
              );
            })
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default HoleDetectionTable;
